import { Box, Chip, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { BulkCreateRestaurantEntryPayload } from "src/data/admin/types/restaurant";
import { colorBorder, colorTextMuted, colorSurface } from "src/design/tokens";

interface Props {
    entries: BulkCreateRestaurantEntryPayload[];
}

const BoolCell = ({ value }: { value: boolean }) => (
    <Chip
        label={value ? "Yes" : "No"}
        size="small"
        color={value ? "success" : "default"}
        variant="outlined"
        sx={{ fontSize: 11 }}
    />
);

const BulkRestaurantTable = ({ entries }: Props) => (
    <Paper
        variant="outlined"
        sx={{
            mt: 3,
            overflow: "auto",
            border: `1px solid ${colorBorder}`,
            backgroundColor: colorSurface,
        }}
    >
        <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: `1px solid ${colorBorder}` }}>
            <Typography variant="caption" color="text.secondary">
                Showing {entries.length} entr{entries.length === 1 ? "y" : "ies"} — scroll horizontally to see all columns
            </Typography>
        </Box>
        <Table size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 600, color: colorTextMuted, fontSize: 12 } }}>
                    <TableCell>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Quartier</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Lat / Lng</TableCell>
                    <TableCell>Booking (min)</TableCell>
                    <TableCell>Seats</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Live</TableCell>
                    <TableCell>Alcohol</TableCell>
                    <TableCell>Featured</TableCell>
                    <TableCell>Animation</TableCell>
                    <TableCell>Booking</TableCell>
                    <TableCell>Insta</TableCell>
                    <TableCell>Tags</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {entries.map((entry, i) => (
                    <TableRow key={i} hover>
                        <TableCell sx={{ color: colorTextMuted, fontSize: 11 }}>{i + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{entry.name}</TableCell>
                        <TableCell>{entry.cityName}</TableCell>
                        <TableCell>{entry.quartier}</TableCell>
                        <TableCell>
                            <Chip label={entry.price} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                        </TableCell>
                        <TableCell>{entry.phone}</TableCell>
                        <TableCell sx={{ maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {entry.address}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, color: colorTextMuted }}>
                            {entry.latitude.toFixed(4)}, {entry.longitude.toFixed(4)}
                        </TableCell>
                        <TableCell>{entry.bookingDuration}</TableCell>
                        <TableCell>{entry.numberOfSeats}</TableCell>
                        <TableCell>{entry.paymentMethods}</TableCell>
                        <TableCell>⭐ {entry.googleRating}</TableCell>
                        <TableCell><BoolCell value={entry.live} /></TableCell>
                        <TableCell><BoolCell value={entry.servesAlcohol} /></TableCell>
                        <TableCell><BoolCell value={entry.featured} /></TableCell>
                        <TableCell><BoolCell value={entry.hasAnimation} /></TableCell>
                        <TableCell><BoolCell value={entry.bookingEnabled} /></TableCell>
                        <TableCell sx={{ color: colorTextMuted, fontSize: 11 }}>
                            {entry.instaTag || "—"}
                        </TableCell>
                        <TableCell>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 200 }}>
                                {entry.tags.length === 0
                                    ? <Typography variant="caption" color="text.disabled">—</Typography>
                                    : entry.tags.map((tag) => (
                                        <Chip key={tag} label={tag} size="small" sx={{ fontSize: 10 }} />
                                    ))
                                }
                            </Box>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </Paper>
);

export default BulkRestaurantTable;
